import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useUserStore } from '../stores/user';

export function Navbar() {
  const navigate = useNavigate();
  const { user, isAuthenticated, signOut } = useAuth();
  const { credits } = useUserStore();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  const navTo = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
        scrolled ? 'bg-cream/75 backdrop-blur-2xl border-b border-surface-border shadow-card' : 'bg-transparent'
      }`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-8 pt-3">
        <div className="app-shell-panel flex items-center justify-between px-4 sm:px-5 h-[72px]">
          {/* Logo */}
          <Link to={isAuthenticated ? '/app' : '/'} className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-rose to-rose-dark text-white flex items-center justify-center shadow-warm">
              <span className="font-serif text-lg" style={{ fontWeight: 700 }}>P</span>
            </div>
            <div>
              <div className="font-serif text-2xl text-ink leading-none" style={{ fontWeight: 600 }}>
                Photo<span className="text-rose">Feeler</span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-ink-faint mt-1">
                Dating + LinkedIn Studio
              </div>
            </div>
          </Link>

          {/* Desktop Right */}
          <div className="hidden sm:flex items-center gap-5">
            {isAuthenticated ? (
              <>
                <Link to="/blog" className="text-xs font-sans text-ink-faint hover:text-ink transition-colors">Blog</Link>
                <Link to="/compare" className="text-xs font-sans text-ink-faint hover:text-ink transition-colors">Compare</Link>
                <div className="w-px h-4 bg-surface-border" />
                <span className="hero-chip py-1.5 px-3 text-xs">
                  <span className="font-medium text-ink">{credits}</span> credit
                  {credits !== 1 ? 's' : ''}
                </span>
                <button
                  onClick={() => navigate('/purchase')}
                  className="text-xs font-sans font-medium text-rose hover:text-rose-dark transition-colors"
                >
                  Buy Credits
                </button>
                <div className="w-px h-4 bg-surface-border" />
                <Link to="/account" className="text-xs text-ink-faint hover:text-ink font-sans transition-colors">
                  {user?.email?.split('@')[0]}
                </Link>
                <button
                  onClick={handleLogout}
                  className="text-xs text-ink-faint hover:text-ink font-sans transition-colors"
                >
                  Log out
                </button>
              </>
            ) : (
              <div className="flex items-center gap-5">
                <Link to="/dating" className="text-sm text-ink-muted hover:text-ink font-sans transition-colors">Dating</Link>
                <Link to="/linkedin" className="text-sm text-ink-muted hover:text-ink font-sans transition-colors">LinkedIn</Link>
                <Link to="/blog" className="text-sm text-ink-muted hover:text-ink font-sans transition-colors">Blog</Link>
                <Link
                  to="/?auth=signin"
                  className="text-sm text-ink-muted hover:text-ink font-sans transition-colors"
                >
                  Sign in
                </Link>
                <Link to="/?auth=1" className="btn-primary text-xs px-5 py-2.5">
                  Get Started
                </Link>
              </div>
            )}
          </div>

          {/* Mobile */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="sm:hidden text-ink p-2"
            aria-label="Toggle menu"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
              {mobileOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 8h16M4 16h16" />
              )}
            </svg>
          </button>
        </div>

        {mobileOpen && (
          <div className="sm:hidden mt-3 app-shell-panel px-4 pb-5 pt-3 space-y-1 animate-fade-in">
            {isAuthenticated ? (
              <>
                <div className="flex justify-between text-sm py-2">
                  <span className="text-ink-faint">{user?.email?.split('@')[0]}</span>
                  <span className="text-ink-muted">{credits} credits</span>
                </div>
                <button onClick={() => navTo('/app')} className="w-full text-left text-sm text-ink-muted py-2">Home</button>
                <button onClick={() => navTo('/compare')} className="w-full text-left text-sm text-ink-muted py-2">Compare</button>
                <button onClick={() => navTo('/blog')} className="w-full text-left text-sm text-ink-muted py-2">Blog</button>
                <button onClick={() => navTo('/purchase')} className="w-full text-left text-sm text-ink-muted py-2">Buy Credits</button>
                <button onClick={() => navTo('/account')} className="w-full text-left text-sm text-ink-muted py-2">Account</button>
                <div className="border-t border-surface-border pt-2 mt-2">
                  <button onClick={handleLogout} className="w-full text-left text-sm text-ink-faint py-2">Log out</button>
                </div>
              </>
            ) : (
              <>
                <button onClick={() => navTo('/dating')} className="w-full text-left text-sm text-ink-muted py-2">Dating</button>
                <button onClick={() => navTo('/linkedin')} className="w-full text-left text-sm text-ink-muted py-2">LinkedIn</button>
                <button onClick={() => navTo('/blog')} className="w-full text-left text-sm text-ink-muted py-2">Blog</button>
                <button onClick={() => navTo('/?auth=signin')} className="w-full text-left text-sm text-ink-muted py-2">Sign in</button>
                <button onClick={() => navTo('/?auth=1')} className="w-full text-center btn-primary text-xs py-2.5 mt-2">Get Started</button>
              </>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}
